import { Badge, Button } from '@artosera/ui';
import { locale } from '@artosera/shared';
import { AlertTriangle, Check, Clock, Globe, UploadCloud } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import {
  usePublications,
  usePublierMaintenant,
  type EtatPublication,
  type ReglagesSite,
} from '../../../api/site.js';
import { galerieALaction } from '../../../auth/sujet.js';
import type { GalerieSession } from '../../../api/types.js';
import styles from './Tabs.module.css';

export interface PublicationTabProps {
  galerieSlug: string;
  site: ReglagesSite;
  domaine: string;
  galerie: GalerieSession;
}

function formaterDateHeure(date: string, langue: 'fr' | 'en'): string {
  return new Intl.DateTimeFormat(locale(langue), {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Europe/Paris',
  }).format(new Date(date));
}

function teinteEtat(etat: EtatPublication): 'available' | 'sold' | 'deposit' {
  if (etat === 'reussie') return 'available';
  if (etat === 'echouee') return 'sold';
  return 'deposit';
}

/**
 * Publication (S6-c, S6-f, `docs/dev/publication.md` § 6) : état de la
 * dernière publication, bouton « Publier maintenant » (enfile, ne publie pas
 * — D9), historique. `POST …/site/publier` rend `202` avec le travail enfilé ;
 * `usePublications` se rafraîchit toute seule tant qu'une publication est
 * `en_cours`.
 */
export function PublicationTab({
  galerieSlug,
  site,
  domaine,
  galerie,
}: PublicationTabProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'site', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const publications = usePublications(galerieSlug);
  const publier = usePublierMaintenant(galerieSlug);
  const peutPublier = galerieALaction(galerie, 'site:publish');

  const [erreur, setErreur] = useState<string | null>(null);

  const derniere = publications.data?.[0];
  const enCours = derniere?.etat === 'en_cours';
  // La publication la plus récente RÉUSSIE : source de la carte d'état.
  // `site.dernierePublication`/`versionPubliee` viennent d'une requête
  // séparée (`GET …/site`) qui ne se rafraîchit qu'à l'enfilement — pas
  // quand le worker termine. L'historique, lui, se rafraîchit tout seul
  // tant qu'une publication est en cours (`usePublications`).
  const derniereReussie = publications.data?.find((publication) => publication.etat === 'reussie');
  const versionAffichee = derniereReussie?.version ?? site.versionPubliee;
  const dateAffichee = derniereReussie?.termineLe ?? site.dernierePublication;

  async function lancerPublication(): Promise<void> {
    setErreur(null);
    try {
      await publier.mutateAsync(undefined);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  // La marque d'état reprend la dernière publication tentée : en cours,
  // échouée, ou la réussite affichée dans la carte.
  const etatMarque: EtatPublication | 'jamais' = enCours
    ? 'en_cours'
    : derniere?.etat === 'echouee'
      ? 'echouee'
      : dateAffichee
        ? 'reussie'
        : 'jamais';
  const IconeEtat =
    etatMarque === 'en_cours'
      ? Clock
      : etatMarque === 'echouee'
        ? AlertTriangle
        : etatMarque === 'reussie'
          ? Check
          : Globe;

  return (
    <div className={styles.formulaire}>
      <div className={styles.carteEtat}>
        <div className={styles.carteEtatHaut}>
          <span className={styles.etatMarque} data-etat={etatMarque} aria-hidden="true">
            <IconeEtat size={22} strokeWidth={1.5} />
          </span>
          <div className={styles.etatTextes}>
            <p className={styles.carteEtatTexte}>
              {enCours
                ? t('site:publication.enCours')
                : dateAffichee
                  ? t('site:publication.derniere', {
                      date: formaterDateHeure(dateAffichee, langue),
                    })
                  : t('site:publication.jamais')}
            </p>
            <p className={styles.aide}>
              {t('regie:site.publication.version', { version: versionAffichee })}
            </p>
          </div>
          {peutPublier ? (
            <Button onClick={() => void lancerPublication()} loading={publier.isPending || enCours}>
              <UploadCloud size={16} strokeWidth={1.5} aria-hidden />
              {t('site:publication.publier')}
            </Button>
          ) : null}
        </div>

        <ul className={styles.hotes}>
          {(derniere?.hotes.length ? derniere.hotes : [domaine]).map((hote) => (
            <li key={hote} className={styles.hote}>
              <Globe size={12} strokeWidth={1.5} aria-hidden />
              {hote}
            </li>
          ))}
        </ul>

        {derniere?.etat === 'echouee' && derniere.erreur ? (
          <p className={styles.erreur} role="alert">
            {derniere.erreur}
          </p>
        ) : null}
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {publier.isSuccess && !erreur ? (
          <p className={styles.succes} role="status">
            {t('regie:site.publication.enfilee')}
          </p>
        ) : null}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitre}>{t('regie:site.publication.historiqueTitre')}</h2>
        {publications.isLoading ? (
          <p className={styles.vide}>{t('commun:chargement')}</p>
        ) : publications.data?.length === 0 || !publications.data ? (
          <p className={`${styles.tableauCadre} ${styles.vide}`}>
            {t('regie:site.publication.aucunePublication')}
          </p>
        ) : (
          <div className={styles.tableauCadre}>
            <table className={`${styles.tableau} ${styles.tableauFiches}`}>
              <thead>
                <tr>
                  <th scope="col">{t('regie:site.publication.colonneVersion')}</th>
                  <th scope="col">{t('regie:site.publication.colonneEtat')}</th>
                  <th scope="col">{t('regie:site.publication.colonneDate')}</th>
                  <th scope="col">{t('regie:site.publication.colonneDuree')}</th>
                  <th scope="col">{t('regie:site.publication.colonneFichiers')}</th>
                </tr>
              </thead>
              <tbody>
                {publications.data.map((publication) => (
                  <tr key={publication.id}>
                    <td
                      className={styles.numero}
                      data-intitule={t('regie:site.publication.colonneVersion')}
                    >
                      {publication.version}
                    </td>
                    <td data-intitule={t('regie:site.publication.colonneEtat')}>
                      <Badge tone={teinteEtat(publication.etat)}>
                        {t(`regie:site.publication.etats.${publication.etat}`)}
                      </Badge>
                    </td>
                    <td data-intitule={t('regie:site.publication.colonneDate')}>
                      {formaterDateHeure(publication.debuteLe, langue)}
                    </td>
                    <td
                      className={styles.attenue}
                      data-intitule={t('regie:site.publication.colonneDuree')}
                    >
                      {publication.dureeMs > 0
                        ? t('regie:site.publication.dureeSecondes', {
                            secondes: Math.round(publication.dureeMs / 1000),
                          })
                        : '—'}
                    </td>
                    <td
                      className={`${styles.attenue} ${styles.cellulePleine}`}
                      data-intitule={t('regie:site.publication.colonneFichiers')}
                    >
                      {publication.etat === 'reussie'
                        ? t('regie:site.publication.bilan', {
                            fichiers: publication.fichiers,
                            medias: publication.medias,
                            polices: publication.polices,
                          })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
